import { useState, useMemo, useCallback } from 'react';
import type { Dictamen } from '../views/Dictamenes/types';

export interface FiltrosDictamenes {
  texto: string;
  tipo: string;
  fechaIndividual: string;
  fechaInicio: string;
  fechaFin: string;
}

export interface UseDictamenesFiltrosReturn {
  filtros: FiltrosDictamenes;
  dictamenesFiltrados: Dictamen[];
  totalFiltrados: number;
  hayFiltrosActivos: boolean;
  actualizarFiltro: (campo: keyof FiltrosDictamenes, valor: string) => void;
  limpiarFiltros: () => void;
  validarRangoFechas: () => { valido: boolean; mensaje?: string };
}

const filtrosIniciales: FiltrosDictamenes = {
  texto: '',
  tipo: '',
  fechaIndividual: '',
  fechaInicio: '',
  fechaFin: ''
};

export function useDictamenesFiltros(dictamenes: Dictamen[]): UseDictamenesFiltrosReturn {
  const [filtros, setFiltros] = useState<FiltrosDictamenes>(filtrosIniciales);

  const actualizarFiltro = useCallback((campo: keyof FiltrosDictamenes, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  }, []);

  const limpiarFiltros = useCallback(() => {
    setFiltros(filtrosIniciales);
  }, []);

  const validarRangoFechas = useCallback(() => {
    if (filtros.fechaInicio && filtros.fechaFin) {
      const inicio = new Date(filtros.fechaInicio);
      const fin = new Date(filtros.fechaFin);
      
      if (inicio > fin) {
        return { valido: false, mensaje: 'La fecha de inicio debe ser anterior a la fecha fin' };
      }
      
      const diferenciaDias = (fin.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24);
      if (diferenciaDias > 365) {
        return { valido: false, mensaje: 'El rango de fechas no puede ser mayor a 1 año' };
      }
    }
    return { valido: true };
  }, [filtros.fechaInicio, filtros.fechaFin]);

  const dictamenesFiltrados = useMemo(() => {
    return dictamenes.filter(d => {
      // Filtro de texto
      if (filtros.texto) {
        const busquedaLimpia = filtros.texto.toLowerCase().replace(/[\s\/\-_]/g, '');
        const folioLimpio = (d.folio || '').toLowerCase().replace(/[\s\/\-_]/g, '');
        const nombreLimpio = (d.nombre || '').toLowerCase().replace(/[\s\/\-_]/g, '');
        const operadorLimpio = (d.operador || '').toLowerCase().replace(/[\s\/\-_]/g, '');
        
        const coincideTexto = nombreLimpio.includes(busquedaLimpia) ||
                             operadorLimpio.includes(busquedaLimpia) ||
                             folioLimpio.includes(busquedaLimpia);
        
        if (!coincideTexto) return false;
      }

      // Filtro por tipo
      if (filtros.tipo && !(d.tipo || '').toLowerCase().includes(filtros.tipo.toLowerCase())) {
        return false;
      }

      // Filtro por fecha individual (tiene prioridad sobre rango)
      if (filtros.fechaIndividual) {
        const fechaDictamen = d.fechaSolicitud?.split('T')[0];
        if (fechaDictamen !== filtros.fechaIndividual) return false;
      } else if (filtros.fechaInicio || filtros.fechaFin) {
        // Filtro por rango de fechas
        const fechaDictamen = d.fechaSolicitud ? new Date(d.fechaSolicitud) : null;
        if (!fechaDictamen) return false;

        if (filtros.fechaInicio) {
          const fechaInicio = new Date(filtros.fechaInicio);
          if (fechaDictamen < fechaInicio) return false;
        }

        if (filtros.fechaFin) {
          const fechaFin = new Date(filtros.fechaFin);
          fechaFin.setHours(23, 59, 59, 999); // Incluir todo el día
          if (fechaDictamen > fechaFin) return false;
        }
      }

      return true;
    });
  }, [dictamenes, filtros]);

  const hayFiltrosActivos = useMemo(() => {
    return Object.values(filtros).some(valor => valor !== '');
  }, [filtros]);

  return {
    filtros,
    dictamenesFiltrados,
    totalFiltrados: dictamenesFiltrados.length,
    hayFiltrosActivos,
    actualizarFiltro,
    limpiarFiltros,
    validarRangoFechas
  };
}